import { CheckCircleOutlined, EditOutlined } from '@ant-design/icons/lib'
import { Col, Divider, Row, Space } from 'antd'
import AttributeSegmentationLink from 'app/components/Core/CategorySegment/AttributeSegmentation/Link'
import { MemberProps } from 'app/components/Core/convention'
import Availability from 'app/components/Core/LineItem/Availability/Availability'
import LineItemImage from 'app/components/Core/LineItem/Image'
import SubItemDetail from 'app/components/Core/SubItem/Detail'
import KDateInfo from 'app/components/Modules/DataDisplay/KDateInfo'
import KPriceInfo from 'app/components/Modules/DataDisplay/KPriceInfo'
import KTimeInfo from 'app/components/Modules/DataDisplay/KTimeInfo'
import KItem from 'app/components/Modules/General/KItem'
import Button from 'app/components/UI-Elements/General/Button'
import { RecordType } from 'app/constants/GlobalTypes'
import useTranslation from 'app/lib/hooks/useTranslation'
import * as React from 'react'
import testIds from 'test_ids.json'

type Props = {
  onClick?: () => void
  showPrice?: boolean
  showAvailability?: boolean
}

// used in rental update/checkout page -> with more details and interactive
const DetailItem: React.FC<MemberProps<ILineItem, Props>> = ({
  member,
  props: { onClick, showPrice, showAvailability = true }
}) => {
  const { tButtonTitle } = useTranslation({ recordType: RecordType.line_item })
  const { tRecordType } = useTranslation({ recordType: RecordType.attachment })

  return (
    <Row>
      <Col span={24}>
        <Divider />
      </Col>
      <Col span={24}>
        <KItem
          title={<span className="h5 text-large text-black mb-0">{member.name}</span>}
          avatar={<LineItemImage member={member} />}
          description={
            <Space direction="vertical">
              <If condition={member.categorySegment?.has_attribute_segmentations}>
                <AttributeSegmentationLink
                  member={{
                    name: member.categorySegment.name,
                    id: member.categorySegment.id
                  }}>
                  {tButtonTitle({ id: 'tech_details' })}
                </AttributeSegmentationLink>
              </If>
              <Space direction="horizontal" wrap>
                <KDateInfo dates={member} />
                <KTimeInfo member={member} />
                {showAvailability && <Availability member={member} />}
              </Space>
            </Space>
          }
          extra={
            <Space direction="vertical" align="end">
              <If condition={showPrice}>
                <KPriceInfo member={member} props={{ type: 'span' }} />
              </If>
              <Button
                data-testid={testIds.checkout.machines.line_item.edit_button}
                icon={<EditOutlined />}
                type="secondary"
                onClick={onClick}
              />
            </Space>
          }
        />
      </Col>
      <If condition={member.sub_items?.length}>
        <Col span={24}>
          <Divider orientation="left">
            <span className="bold text-black text-md">{tRecordType({})}</span>
          </Divider>
        </Col>
        <Col span={24}>
          <Row gutter={[0, 12]}>
            {member.sub_items.map((item) => (
              <Col key={item.id} xs={{ span: 23, offset: 1 }} md={showPrice ? undefined : 11}>
                <SubItemDetail
                  member={item}
                  props={{
                    avatar: <CheckCircleOutlined className="text-xl " />,
                    description: (
                      <Space wrap>
                        <KDateInfo
                          dates={{ start_date: item.start_date, return_date: item.return_date }}
                        />
                        {showAvailability && <Availability member={item} />}
                      </Space>
                    ),
                    extra: (
                      <If condition={showPrice}>
                        <KPriceInfo member={item} props={{ type: 'span' }} />
                      </If>
                    ),
                    verticalAlign: false,
                    className: 'ant-list-item--unstyled-title'
                  }}
                />
              </Col>
            ))}
          </Row>
        </Col>
      </If>
    </Row>
  )
}

export default DetailItem
